import React, { useCallback, useMemo } from "react";

import { Box, SliderProps, Stack, Typography, styled } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { Datapoint, SeriesType } from "../hooks/useVisualisationData";
import { useAppContext } from "../context/AppContext";

const NAME = "TimelineChart";
const patientTimelineSliderClasses = {
  rail: `${NAME}-rail`,
  mark: `${NAME}-mark`,
};

type MarkType = { id: string; color: string; name: string; value: number };
type MarkGroupType = { value: number; marks: MarkType[] };

const Root = styled(Box)(({ theme }) => ({
  background: theme.palette.background.default,
  border: `0.75px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 4,
  padding: theme.spacing(0, 1.5),
  position: "relative",
  [`& .${patientTimelineSliderClasses.rail}`]: {
    position: "relative",
    height: theme.spacing(2.5),
  },
}));

const Tick = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(10),
  lineHeight: 1,
  opacity: 0.33,
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
}));

const Ticks = React.memo(() =>
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
    <Tick
      color="text.disabled"
      key={`tick-${num}`}
      style={{ left: `${num * 10}%` }}
      variant="caption"
    >
      {num}
    </Tick>
  ))
);

const Mark = React.memo(
  styled("div")<{
    bgColor: string;
    display: string;
    border?: string;
  }>(({ bgColor, display, theme, border }) => ({
    background: bgColor,
    border: border || undefined,
    borderRadius: theme.shape.borderRadius * 2,
    display,
    height: theme.spacing(1.5),
    position: "relative",
    width: theme.spacing(1.5),
    transition: theme.transitions.create(["background"], {
      duration: theme.transitions.duration.short,
    }),
    cursor: "pointer",
    "&:hover": {
      zIndex: 1,
    },
  }))
);

const MarkGroup = React.memo(
  styled(Stack)(() => ({
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
  }))
);

const Marks: React.FC<{
  data: { value: number; marks: MarkType[] };
}> = React.memo(({ data }) => {
  const { legend, setLegend, toggleTimelinePopover } = useAppContext();
  const onMarkGroupClick = useCallback(
    (marks: MarkType[]) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      toggleTimelinePopover({
        anchorEl: e.currentTarget,
        enforceOpen: true,
        marks,
      });
    },
    [toggleTimelinePopover]
  );

  const onMarkGroupMouseEnter = useCallback(
    (marks: MarkType[]) => (e: React.MouseEvent<HTMLDivElement>) => {
      toggleTimelinePopover({
        anchorEl: e.currentTarget,
        enforceOpen: false,
        marks,
      });
    },
    [toggleTimelinePopover]
  );

  const onMarkGrupMouseLeave = useCallback(() => {
    toggleTimelinePopover();
  }, [toggleTimelinePopover]);

  const onMarkMouseEnter = (seriesId: string) => () => {
    setLegend((s) => ({ ...s, focused: seriesId }));
  };

  const onMarkMouseLeave = () => {
    setLegend((s) => ({ ...s, focused: null }));
  };

  const onMarkClick = (seriesId: string) => () => {
    setLegend((s) => ({ ...s, focused: seriesId }));
  };

  const availableMarks = data.marks.filter(
    (item) => !legend?.hidden?.includes(item.id)
  );

  return (
    <MarkGroup
      direction="row"
      flexWrap="nowrap"
      onClick={onMarkGroupClick(data.marks)}
      onMouseEnter={onMarkGroupMouseEnter(data.marks)}
      onMouseLeave={onMarkGrupMouseLeave}
      spacing={{ xs: -0.75, md: -0.65, xl: -0.55 }}
      style={{ left: `${data.value * 10}%` }}
    >
      {availableMarks.slice(0, 3).map((mark: MarkType) => {
        return (
          <Mark
            bgColor={
              legend.focused === mark.id || !legend.focused
                ? mark.color
                : "rgba(0, 0, 0, 0.1)"
            }
            display="block"
            key={mark.name}
            onClick={onMarkClick(mark.id)}
            onMouseEnter={onMarkMouseEnter(mark.id)}
            onMouseLeave={onMarkMouseLeave}
          >
            {availableMarks.length > 3 && availableMarks[2].id === mark.id && (
              <AddIcon
                sx={{
                  color: "primary.contrastText",
                  fontSize: 12,
                  left: "50%",
                  position: "absolute",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                }}
              />
            )}
          </Mark>
        );
      })}
    </MarkGroup>
  );
});

export const TimelineChart: React.FC<
  SliderProps & { data: Datapoint["values"]; series: SeriesType[] }
> = ({ data, series }) => {
  const groups: MarkGroupType[] = useMemo(() => {
    const groupedItems = Object.entries(data).reduce<MarkGroupType[]>(
      (acc, [key, { name, value }]) => {
        const group = acc.find((item) => item.value === value);
        const color = series.find((s) => s.id === key)?.color || "red";
        const mark: MarkType = {
          color,
          id: key,
          name,
          value,
        };
        if (group) {
          group.marks.push(mark);
        } else {
          acc.push({ value, marks: [mark] });
        }
        return acc;
      },
      [] as MarkGroupType[]
    );
    return groupedItems;
  }, [data, series]);

  return (
    <Root>
      <div className={patientTimelineSliderClasses.rail}>
        <Ticks />
        {groups.map((group) => (
          <Marks data={group} key={group.value} />
        ))}
      </div>
    </Root>
  );
};

export default TimelineChart;
